import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { 
  MessageSquare, 
  Clock, 
  Save, 
  Plus, 
  Trash2, 
  ExternalLink, 
  CheckCircle2 
} from 'lucide-react';
import './FormsUnified.css';

export default function ParentsCommunicationForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Communication Channels',
    title: 'Multiple Ways to Stay Connected',
    subtitle: 'We believe in open communication. Connect with us through your preferred channel',
    channels: [
      {
        id: 'google-classroom',
        title: 'Google Classroom',
        description: 'Daily class updates, assignments, and quick teacher communications',
        features: ['Daily Updates', 'Assignment Tracking', 'Direct Messaging'],
        link_label: 'Access Classroom',
        link_url: 'https://classroom.google.com'
      },
      {
        id: 'email-updates',
        title: 'Email Updates',
        description: 'Weekly summaries, important reminders, and event invitations',
        features: ['Weekly Digest', 'Event Reminders', 'Important Notices'],
        link_label: 'Subscribe Now',
        link_url: 'mailto:support@school.com'
      },
      {
        id: 'lms-portal',
        title: 'LMS Portal',
        description: 'Central learning platform for resources, grades, and progress tracking',
        features: ['Learning Resources', 'Grade Tracking', 'Progress Reports'],
        link_label: 'Open Portal',
        link_url: '/lms'
      }
    ]
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    subtitle: false
  });
  const [pendingChannels, setPendingChannels] = useState({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});

  // Input for adding feature tag to a channel
  const [newFeatureText, setNewFeatureText] = useState({});

  const getAuthHeaders = () => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=parents`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => 
          s.section_type === 'parents-communication' || 
          s.section_type === 'communication-channels' || 
          s.section_type === 'communication'
        );
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=parents`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => 
            s.section_type === 'parents-communication' || 
            s.section_type === 'communication-channels' || 
            s.section_type === 'communication'
          );
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);

        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const loadedData = {
          kicker: en.kicker || 'Communication Channels',
          title: en.title || 'Multiple Ways to Stay Connected',
          subtitle: en.subtitle || en.description || formData.subtitle,
          channels: Array.isArray(en.channels) && en.channels.length > 0 ? en.channels : formData.channels
        };

        setFormData(loadedData);
        setOriginalData(loadedData);

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              const pFields = { kicker: false, title: false, subtitle: false };
              const pChannels = {};
              const pDeletes = {};

              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  if (['kicker', 'title', 'subtitle'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                  } else if (c.field_id?.includes('channels.')) {
                    const m = c.field_id.match(/channels\.(\d+)(?:\.(add|update|delete))?/);
                    if (m) {
                      const idx = m[1];
                      const op = m[2] || 'update';
                      if (op === 'delete') {
                        pDeletes[`channel_${idx}`] = true;
                      } else {
                        pChannels[`channel_${idx}`] = true;
                      }
                    }
                  }
                }
              });

              setPendingFields(pFields);
              setPendingChannels(pChannels);
              setPendingDeletes(pDeletes);
            }
          }
        } catch (pe) {
          console.warn('[ParentsCommunicationForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[ParentsCommunicationForm] Load error:', err);
      toast.error('Gagal memuat konten Saluran Komunikasi');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleChannelChange = (index, subField, value) => {
    setFormData(prev => {
      const next = [...prev.channels];
      next[index] = { ...next[index], [subField]: value };
      return { ...prev, channels: next };
    });
  };

  const handleAddChannelFeature = (channelIndex) => {
    const text = (newFeatureText[channelIndex] || '').trim();
    if (!text) return;

    setFormData(prev => {
      const next = [...prev.channels];
      const curFeatures = Array.isArray(next[channelIndex].features) ? [...next[channelIndex].features] : [];
      curFeatures.push(text);
      next[channelIndex] = { ...next[channelIndex], features: curFeatures };
      return { ...prev, channels: next };
    });

    setNewFeatureText(prev => ({ ...prev, [channelIndex]: '' }));
    toast.success('Poin fitur komunikasi ditambahkan');
  };

  const handleRemoveChannelFeature = (channelIndex, featureIndex) => {
    setFormData(prev => {
      const next = [...prev.channels];
      const curFeatures = (next[channelIndex].features || []).filter((_, idx) => idx !== featureIndex);
      next[channelIndex] = { ...next[channelIndex], features: curFeatures };
      return { ...prev, channels: next };
    });
  };

  const handleAddChannel = () => {
    const newChan = {
      id: `channel-${Date.now()}`,
      title: 'Saluran Komunikasi Baru',
      description: 'Penjelasan cara orang tua dapat berkomunikasi dan terhubung...',
      features: ['Fitur Utama 1', 'Fitur Utama 2'],
      link_label: 'Kunjungi',
      link_url: 'https://'
    };

    setFormData(prev => ({
      ...prev,
      channels: [...prev.channels, newChan]
    }));
    toast.success('Saluran baru ditambahkan ke draf');
  };

  const handleRemoveChannel = async (index) => {
    if (formData.channels.length <= 1) {
      toast.warning('Minimal harus ada 1 saluran komunikasi');
      return;
    }
    const channelToRemove = formData.channels[index];
    const isSavedInOriginal = originalData.channels?.some(orig => orig.id === channelToRemove.id || orig.title === channelToRemove.title);

    if (!isSavedInOriginal || !sectionId) {
      if (window.confirm(`Hapus saluran draf "${channelToRemove.title || 'Untitled'}"?`)) {
        setFormData(prev => ({
          ...prev,
          channels: prev.channels.filter((_, idx) => idx !== index)
        }));
        toast.info(`Saluran "${channelToRemove.title}" dihapus dari draf`);
      }
      return;
    }

    if (!window.confirm(`Ajukan penghapusan saluran "${channelToRemove.title || 'Untitled'}" ke Kepala Sekolah?`)) {
      return;
    }

    const key = `channel_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `channels.${index}.delete`,
          field_name: `Hapus Saluran Komunikasi: ${channelToRemove.title}`,
          old_value: channelToRemove,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Permintaan hapus saluran "${channelToRemove.title}" berhasil diajukan untuk approval`);
        setPendingDeletes(prev => ({ ...prev, [key]: true }));
        try {
          sessionStorage.removeItem('cms_page_parents');
          sessionStorage.removeItem('cms_sections_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Gagal mengajukan penghapusan');
      }
    } catch (err) {
      console.error('[ParentsCommunicationForm] Delete error:', err);
      toast.error('Gagal mengajukan hapus: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmitChannel = async (index) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const channel = formData.channels[index];
    const isNew = !originalData.channels?.[index];
    const key = `channel_${index}`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `channels.${index}.${isNew ? 'add' : 'update'}`,
          field_name: isNew ? `Tambah Saluran: ${channel.title}` : `Saluran: ${channel.title}`,
          old_value: isNew ? null : (originalData.channels?.[index] || {}),
          new_value: channel,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Saluran "${channel.title}" berhasil diajukan untuk approval`);
        setPendingChannels(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.channels || [])];
          if (isNew) {
            next.push(channel);
          } else {
            next[index] = channel;
          }
          return { ...prev, channels: next };
        });
        try {
          sessionStorage.removeItem('cms_page_parents');
          sessionStorage.removeItem('cms_sections_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ParentsCommunicationForm] Submit error:', err);
      toast.error('Gagal mengajukan saluran: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmitField = async (field, customValue, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const valToSubmit = customValue !== undefined ? customValue : formData[field];
    const oldVal = originalData[field];

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: fieldDisplayName || (field.charAt(0).toUpperCase() + field.slice(1)),
          old_value: oldVal,
          new_value: valToSubmit,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${fieldDisplayName || field}" berhasil diajukan untuk approval Kepala Sekolah`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: valToSubmit }));
        try {
          sessionStorage.removeItem('cms_page_parents');
          sessionStorage.removeItem('cms_sections_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ParentsCommunicationForm] Submit error:', err);
      toast.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Communication Channels content...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <MessageSquare size={22} />
        </div>
        <div>
          <h2 className="unified-title">Communication Channels</h2>
          <p className="unified-subtitle">
            Kelola saluran komunikasi orang tua dan sekolah (Google Classroom, Email Updates, Portal LMS) lengkap dengan tautan tujuan langsung saat kartu diklik.
          </p>
        </div>
      </div>

      {/* Section Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker}
            placeholder="e.g. Communication Channels"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', formData.kicker, 'Section Kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title}
            placeholder="e.g. Multiple Ways to Stay Connected"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', formData.title, 'Section Title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Subtitle */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Subtitle</label>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <textarea
            className="unified-textarea"
            rows={2}
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={pendingFields.subtitle}
            placeholder="We believe in open communication. Connect with us through your preferred channel..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle', formData.subtitle, 'Section Subtitle')}
            disabled={pendingFields.subtitle || submitting.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} /> {submitting.subtitle ? 'Saving...' : (pendingFields.subtitle ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Channels List Cards */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">Channels ({formData.channels.length})</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Setiap saluran akan tampil sebagai kartu interaktif dengan daftar fitur dan tombol tautan langsung.
            </p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleAddChannel}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px' }}
          >
            <Plus size={16} /> Tambah Saluran Baru
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '16px' }}>
          {formData.channels.map((channel, idx) => {
            const isPending = pendingChannels[`channel_${idx}`];
            const isPendingDelete = pendingDeletes[`channel_${idx}`];
            const isSubmitting = submitting[`channel_${idx}`];

            return (
              <div
                key={idx}
                style={{
                  border: isPendingDelete ? '1.5px dashed #ef4444' : (isPending ? '1.5px dashed #f59e0b' : '1px solid #e2e8f0'),
                  borderRadius: '14px',
                  padding: '20px',
                  background: isPendingDelete ? '#fef2f2' : (isPending ? '#fffdf7' : '#ffffff'),
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px'
                }}
              >
                {/* Header Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '12px' }}>
                      SALURAN #{idx + 1}
                    </span>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                      {channel.title || 'Saluran Tanpa Judul'}
                    </h4>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isPendingDelete ? (
                      <span className="unified-pending-pill" style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#fca5a5' }}>
                        <Clock size={12} /> Pending Delete
                      </span>
                    ) : isPending ? (
                      <span className="unified-pending-pill">
                        <Clock size={12} /> Pending Approval
                      </span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => handleRemoveChannel(idx)}
                      disabled={isPending || isPendingDelete || isSubmitting}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: '#ef4444', 
                        padding: '4px', 
                        cursor: (isPending || isPendingDelete || isSubmitting) ? 'not-allowed' : 'pointer',
                        opacity: (isPending || isPendingDelete || isSubmitting) ? 0.4 : 1
                      }}
                      title="Hapus Saluran"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Title & Description */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '14px' }}>
                  <div>
                    <label className="unified-label">Nama Saluran</label>
                    <input
                      type="text"
                      className="unified-input"
                      value={channel.title || ''}
                      disabled={isPending || isPendingDelete}
                      onChange={(e) => handleChannelChange(idx, 'title', e.target.value)}
                      placeholder="e.g. Google Classroom"
                    />
                  </div>
                  <div>
                    <label className="unified-label">Deskripsi Saluran</label>
                    <input
                      type="text"
                      className="unified-input"
                      value={channel.description || ''}
                      disabled={isPending || isPendingDelete}
                      onChange={(e) => handleChannelChange(idx, 'description', e.target.value)}
                      placeholder="Penjelasan ringkas peruntukan saluran ini..."
                    />
                  </div>
                </div>

                {/* Link Label & Link URL */}
                <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '14px', background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <div>
                    <label className="unified-label">Label Tautan / Tombol</label>
                    <input
                      type="text"
                      className="unified-input"
                      value={channel.link_label || ''}
                      disabled={isPending || isPendingDelete}
                      onChange={(e) => handleChannelChange(idx, 'link_label', e.target.value)}
                      placeholder="e.g. Access Classroom"
                    />
                  </div>
                  <div>
                    <label className="unified-label">
                      Tautan URL Tujuan (Link Target) *
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        className="unified-input"
                        value={channel.link_url || ''}
                        disabled={isPending || isPendingDelete}
                        onChange={(e) => handleChannelChange(idx, 'link_url', e.target.value)}
                        placeholder="e.g. https://classroom.google.com atau mailto:support@school.com"
                        style={{ flex: 1 }}
                      />
                      {channel.link_url && (
                        <a
                          href={channel.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="unified-secondary-btn"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', textDecoration: 'none', padding: '0 12px', whiteSpace: 'nowrap' }}
                        >
                          Buka Link <ExternalLink size={13} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Features Tags */}
                <div>
                  <label className="unified-label" style={{ marginBottom: '6px', display: 'block' }}>
                    Poin Fitur / Fasilitas Saluran:
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                    {(channel.features || []).map((feat, fIdx) => (
                      <span key={fIdx} style={{ background: '#eff6ff', color: '#1e40af', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <CheckCircle2 size={13} color="#2563eb" /> {feat}
                        <button
                          type="button"
                          onClick={() => handleRemoveChannelFeature(idx, fIdx)}
                          disabled={isPending || isPendingDelete}
                          style={{ background: 'none', border: 'none', color: '#93c5fd', cursor: (isPending || isPendingDelete) ? 'not-allowed' : 'pointer', padding: 0 }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </span>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', maxWidth: '420px' }}>
                    <input
                      type="text"
                      className="unified-input"
                      value={newFeatureText[idx] || ''}
                      disabled={isPending || isPendingDelete}
                      onChange={(e) => setNewFeatureText(prev => ({ ...prev, [idx]: e.target.value }))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddChannelFeature(idx);
                        }
                      }}
                      placeholder="Tambah poin fitur (misal: 'Daily Updates')..."
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    />
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={() => handleAddChannelFeature(idx)}
                      disabled={isPending || isPendingDelete}
                      style={{ fontSize: '12px', padding: '6px 12px', whiteSpace: 'nowrap' }}
                    >
                      + Tambah
                    </button>
                  </div>
                </div>

                {/* Card Submit Action */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={() => handleSubmitChannel(idx)}
                    disabled={isPending || isPendingDelete || isSubmitting}
                  >
                    <Save size={14} /> {isSubmitting ? 'Saving...' : (isPendingDelete ? 'Pending Delete' : (isPending ? 'Pending' : 'Save Channel'))}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
